import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';

interface Member {
  auth_id: string;
  full_name: string;
  avatar_url: string | null;
  city: string;
  job_type?: string;
  company?: string;
}

interface LatestMembersProps {
  members: Member[];
  isLoading?: boolean;
}

const LatestMembers: React.FC<LatestMembersProps> = ({ members, isLoading = false }) => {
  const navigate = useNavigate();
  
  const handleMemberClick = (memberId: string) => {
    navigate(`/dashboard/members/${memberId}`);
  };
  
  const handleViewAllClick = () => {
    navigate('/dashboard/match');
  };
  
  if (isLoading) {
    return (
      <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 w-40 bg-dark-300 rounded"></div>
          <div className="h-6 w-24 bg-dark-300 rounded"></div>
        </div>
        <div className="flex overflow-x-auto space-x-4 pb-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-dark-300 rounded-lg h-48 w-48 flex-shrink-0"></div>
          ))}
        </div>
      </div>
    );
  }
  
  if (members.length === 0) return null;
  
  return (
    <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Users className="h-5 w-5 text-gold-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">Latest Members</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewAllClick}
          className="text-dark-800 hover:text-white"
        >
          View All
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      
      <div className="flex overflow-x-auto pb-2 space-x-4 no-scrollbar">
        {members.map((member) => (
          <div
            key={member.auth_id}
            className="bg-dark-300 rounded-lg p-4 hover:bg-dark-400 transition-colors border border-dark-400 hover:border-gold-400 flex-shrink-0 w-[200px]"
            onClick={() => handleMemberClick(member.auth_id)}
          >
            <div className="flex flex-col items-center">
              <div className="mb-3 cursor-pointer">
                <img
                  src={member.avatar_url || 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'}
                  alt={member.full_name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-dark-400 hover:border-gold-400 transition-colors"
                  onError={(e) => {
                    e.currentTarget.src = 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png';
                  }}
                />
              </div>
              <div className="w-full text-center">
                <h4 className="font-medium text-white hover:text-gold-400 transition-colors cursor-pointer text-sm truncate">
                  {member.full_name}
                </h4>
                {member.city && (
                  <div className="text-dark-800 text-xs mt-1 truncate">
                    {member.city}
                  </div>
                )}
                {(member.job_type || member.company) && (
                  <div className="text-dark-800 text-xs mt-0.5 truncate">
                    {member.job_type}
                    {member.job_type && member.company && ' at '}
                    {member.company}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-dark-400">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMemberClick(member.auth_id);
                }}
                className="w-full bg-dark-400 hover:bg-dark-300 dark:hover:bg-dark-500 text-white text-xs py-1"
                size="sm"
              >
                View Profile
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LatestMembers;